import type { RequirementFlagListRow } from '@repo/sdk/gen'
import { zRequirementFlagListForResidenceData } from '@repo/sdk/zod'
import { Badge } from '@repo/ui/badge'
import { DataTableColumnSettings } from '@repo/ui/components/data-table/column-settings'
import {
	DataTable,
	useDataTableColumnState
} from '@repo/ui/components/data-table/data-table'
import type { DataTableColumn } from '@repo/ui/components/data-table/types'
import { ListLayout } from '@repo/ui/components/list-layout'
import { useQuery } from '@tanstack/react-query'
import {
	createFileRoute,
	Link,
	stripSearchParams,
	useNavigate
} from '@tanstack/react-router'
import { z } from 'zod'
import { useTranslation } from '@/lib/i18n'
import { usePageSize } from '@/stores/use-page-size'
import { useWorkingCall } from '../../-hooks/use-working-call'
import { formatRomeLastDay } from '../../-lib/rome-time'
import {
	FLAG_CODE_LABEL,
	FLAG_STATE_LABEL,
	FLAG_STATE_TONE,
	flagFacts
} from '../-lib/flag-labels'
import { FlagsFilters } from './-components/flags-filters'
import { flagsListQueryOptions } from './-query/query-options'

const filterShape =
	zRequirementFlagListForResidenceData.shape.body.shape.filters.shape

const searchSchema = z.object({
	state: filterShape.state.optional(),
	code: filterShape.code.optional(),
	quota: filterShape.quota.optional(),
	page: z.number().int().min(1).default(1)
})

export const Route = createFileRoute(
	'/(private)/bando/$residenceId/segnalazioni/'
)({
	component: FlagsPage,
	validateSearch: searchSchema,
	search: {
		middlewares: [stripSearchParams(searchSchema.parse({}))]
	}
})

const EMPTY_ROWS: RequirementFlagListRow[] = []

/**
 * The flags of the residence (FFD 2.6, step 1): the value declared beside the
 * threshold of the call, so most are judged at a glance, those to verify
 * first. Only the system makes a flag: there is nothing to add here.
 */
function FlagsPage() {
	const { t, i18n } = useTranslation()
	const { residenceId } = Route.useParams()
	const params = Route.useSearch()
	const navigate = useNavigate({ from: Route.fullPath })
	const pageSize = usePageSize((s) => s.pageSize)
	const setPageSize = usePageSize((s) => s.setPageSize)

	const columns: DataTableColumn<RequirementFlagListRow>[] = [
		{
			id: 'protocol',
			header: t('admin-flags:columns.protocol'),
			cell: (row) => <span className='tabular-nums'>{row.protocol}</span>,
			minWidth: 80,
			width: 90
		},
		{
			id: 'name',
			header: t('admin-flags:columns.name'),
			cell: (row) => (
				<Link
					className='truncate hover:underline'
					params={{ residenceId, submissionId: row.submissionId }}
					to='/bando/$residenceId/domande/$submissionId'
				>
					{[row.lastName, row.firstName].filter(Boolean).join(' ') ||
						t('admin-flags:facts.not_given')}
				</Link>
			),
			minWidth: 180,
			flex: 2
		},
		{
			id: 'code',
			header: t('admin-flags:columns.code'),
			cell: (row) => (
				<Link
					className='font-medium hover:underline'
					params={{ residenceId, flagId: row.id }}
					to='/bando/$residenceId/segnalazioni/$flagId'
				>
					{t(FLAG_CODE_LABEL[row.code])}
				</Link>
			),
			minWidth: 200,
			flex: 2
		},
		{
			id: 'declared',
			header: t('admin-flags:columns.declared'),
			cell: (row) => (
				<span className='tabular-nums'>
					{flagFacts(row.facts, t, i18n.language).declared}
				</span>
			),
			minWidth: 130,
			flex: 1
		},
		{
			id: 'threshold',
			header: t('admin-flags:columns.threshold'),
			cell: (row) => (
				<span className='text-muted-foreground tabular-nums'>
					{flagFacts(row.facts, t, i18n.language).threshold}
				</span>
			),
			minWidth: 200,
			flex: 2
		},
		{
			id: 'quotas',
			header: t('admin-flags:columns.quotas'),
			cell: (row) =>
				row.quotas
					.map((quota) => t(`admin-applications:quota.${quota}`))
					.join(', '),
			minWidth: 140,
			flex: 1
		},
		{
			id: 'state',
			header: t('admin-flags:columns.state'),
			cell: (row) => (
				<div className='flex flex-col items-start gap-1'>
					<Badge variant={FLAG_STATE_TONE[row.state]}>
						{t(FLAG_STATE_LABEL[row.state])}
					</Badge>
					{row.outcome && (
						<span className='text-muted-foreground text-xs'>
							{t('admin-flags:closed_by', {
								name: row.outcome.by ?? t('admin-flags:someone')
							})}
						</span>
					)}
					{row.reviewing && (
						<span className='text-muted-foreground text-xs'>
							{t('admin-flags:reviewing', {
								name:
									row.reviewing.name ??
									t('admin-flags:someone')
							})}
						</span>
					)}
				</div>
			),
			minWidth: 190,
			flex: 1
		},
		{
			id: 'due',
			header: t('admin-flags:columns.due'),
			cell: (row) => <DueCell row={row} />,
			minWidth: 170,
			flex: 1
		}
	]
	const columnsState = useDataTableColumnState(columns)

	const { callId } = useWorkingCall()
	const query = useQuery(
		flagsListQueryOptions({
			page: params.page,
			limit: pageSize,
			filters: {
				residenceId,
				callId,
				state: params.state,
				code: params.code,
				quota: params.quota
			}
		})
	)
	const hasFilters = Boolean(params.state || params.code || params.quota)

	return (
		<ListLayout
			controlsSlot={
				<>
					<FlagsFilters />
					<DataTableColumnSettings
						columns={columns}
						columnsState={columnsState}
					/>
				</>
			}
			listSlot={
				<DataTable
					columns={columns}
					columnsState={columnsState}
					data={query.data?.data ?? EMPTY_ROWS}
					onRowClick={(row) =>
						navigate({
							params: { residenceId, flagId: row.id },
							to: '/bando/$residenceId/segnalazioni/$flagId'
						})
					}
					pagination={{
						pageIndex: params.page - 1,
						pageCount: query.data?.meta.totalPages ?? 1,
						onPageChange: (next) =>
							navigate({
								search: (prev) => ({ ...prev, page: next + 1 }),
								replace: true
							}),
						pageSize,
						onPageSizeChange: (next) => {
							setPageSize(next)
							navigate({
								search: (prev) => ({ ...prev, page: 1 }),
								replace: true
							})
						}
					}}
					state={{
						loading: query.isPending || query.isPlaceholderData,
						error: query.isError,
						empty: hasFilters
							? t('admin-flags:empty_filtered')
							: t('admin-flags:empty')
					}}
				/>
			}
			title={t('admin-flags:page_title')}
		/>
	)
}

/** The end of the term, and a plain note when it is close: no alarm colours. */
function DueCell({ row }: { row: RequirementFlagListRow }) {
	const { t, i18n } = useTranslation()
	if (!row.dueAt) {
		return (
			<span className='text-muted-foreground text-sm'>
				{t('admin-flags:due.none')}
			</span>
		)
	}
	return (
		<div className='flex flex-col'>
			<span
				className={
					row.dueSoon ? 'font-medium tabular-nums' : 'tabular-nums'
				}
			>
				{t('admin-flags:due.date', {
					date: formatRomeLastDay(row.dueAt, i18n.language)
				})}
			</span>
			{row.dueSoon && (
				<span className='text-muted-foreground text-xs'>
					{t('admin-flags:due.soon')}
				</span>
			)}
		</div>
	)
}
